"use client";

import * as React from "react";
import { cn } from "@/lib/utils";
import {
  fieldVariantClasses,
  type FieldVariant,
} from "./field-variants";

export interface InputProps
  extends Omit<
      React.InputHTMLAttributes<HTMLInputElement>,
      "className" | "size"
    > {
  /** @deprecated rsuite compat — ignored when set to "textarea"; prefer Textarea */
  as?: string;
  rows?: number;
  style?: React.CSSProperties;
  label?: string;
  error?: string;
  helperText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  size?: "default" | "xxs" | "xs" | "sm" | "compact" | "filter" | "md" | "lg";
  variant?: FieldVariant;
  /** Sin borde ni label propio; para usar dentro de un contenedor compuesto. */
  embedded?: boolean;
  /** Contenido en la esquina superior derecha del campo (p. ej. botón IA). */
  topEnd?: React.ReactNode;
  className?: string;
}

function resolveInputSize(size: InputProps["size"]) {
  if (size === "xxs") return "xxs";
  if (size === "xs") return "xs";
  if (size === "sm" || size === "compact") return "sm";
  if (size === "filter") return "filter";
  return "default";
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      type,
      label,
      error,
      helperText,
      leftIcon,
      rightIcon,
      size = "sm",
      variant = "subtle",
      id,
      value,
      defaultValue,
      onChange,
      onFocus,
      onBlur,
      onAnimationStart,
      className,
      embedded = false,
      topEnd,
      as: _as,
      rows: _rows,
      ...props
    },
    ref
  ) => {
    const generatedId = React.useId();
    const inputId = id ?? generatedId;
    const resolvedSize = resolveInputSize(size);
    const isXxs = resolvedSize === "xxs";
    const isXs = resolvedSize === "xs";
    const isSm = resolvedSize === "sm";
    const isFilter = resolvedSize === "filter";
    const colors = fieldVariantClasses(variant, { error: Boolean(error) });

    const [isFocused, setIsFocused] = React.useState(false);
    const [hasValue, setHasValue] = React.useState(
      Boolean(value !== undefined ? value : defaultValue)
    );

    React.useEffect(() => {
      if (value !== undefined) setHasValue(Boolean(value));
    }, [value]);

    const isFloating = isFocused || hasValue;
    const hasTopEnd = Boolean(topEnd);

    return (
      <div className={cn("w-full", className)}>
        <div className="relative w-full">
          {leftIcon && (
            <div className={cn(
              "pointer-events-none absolute top-1/2 z-10 -translate-y-1/2 text-muted-foreground",
              isXxs ? "left-2 h-3 w-3" : isXs ? "left-2.5 h-3.5 w-3.5" : isSm || isFilter ? "left-3 h-3.5 w-3.5" : "left-3.5 h-4 w-4"
            )}>
              {leftIcon}
            </div>
          )}

          <input
            id={inputId}
            type={type}
            ref={ref}
            value={value}
            defaultValue={defaultValue}
            className={cn(
              "peer w-full text-sm leading-5 text-foreground outline-none transition-all duration-200",
              embedded
                ? cn(
                    "h-full min-h-9 rounded-none border-0 bg-transparent shadow-none",
                    isXxs ? "text-[11px] leading-4" : isXs ? "text-xs leading-4" : isSm || isFilter ? "text-xs leading-4" : "text-sm leading-5",
                    isXxs ? (leftIcon ? "pl-7 pr-2" : "px-2") : isXs ? (leftIcon ? "pl-8 pr-2" : "px-2.5") : isSm || isFilter ? (leftIcon ? "pl-9 pr-3" : "px-3.5") : (leftIcon ? "pl-10 pr-4" : "px-4"),
                    rightIcon && !leftIcon && (isXxs ? "pr-7" : isXs ? "pr-8" : isSm || isFilter ? "pr-9" : "pr-10"),
                    rightIcon && leftIcon && (isXxs ? "pr-7" : isXs ? "pr-8" : isSm || isFilter ? "pr-9" : "pr-10"),
                    !rightIcon && leftIcon && (isXxs ? "pr-2" : isXs ? "pr-2.5" : isSm || isFilter ? "pr-3.5" : "pr-4"),
                    "placeholder:text-slate-400 dark:placeholder:text-zinc-500",
                    "focus:ring-0 focus:ring-offset-0"
                  )
                : cn(
                    "rounded-2xl border bg-white/70 shadow-[0_1px_2px_rgba(15,23,42,0.05)] dark:bg-zinc-950/70 dark:shadow-[0_1px_2px_rgba(0,0,0,0.35)]",
                    isXxs
                      ? "h-7 py-0 text-[11px] ring-offset-background"
                      : isXs
                        ? "h-9 py-0 text-xs ring-offset-background"
                        : isFilter
                          ? "h-11 py-0 text-xs ring-offset-background"
                          : isSm
                            ? "h-10 py-0 text-xs ring-offset-background"
                            : "h-[58px] py-0 ring-offset-background",
                    label
                      ? "placeholder:text-transparent"
                      : "placeholder:text-slate-400 dark:placeholder:text-zinc-500",
                    "focus:ring-1 focus:ring-offset-0",
                    colors.border,
                    colors.focusBorder,
                    colors.focusRing,
                    isXxs ? (leftIcon ? "pl-7 pr-2" : "px-2") : isXs ? (leftIcon ? "pl-8 pr-3" : "px-2.5") : isSm || isFilter ? (leftIcon ? "pl-9 pr-3.5" : "px-3") : (leftIcon ? "pl-10 pr-4" : "px-4"),
                    rightIcon && (isXxs ? "pr-7" : isXs ? "pr-8" : "pr-10"),
                    hasTopEnd && !rightIcon && (isXxs ? "pr-7" : isXs ? "pr-6" : isSm || isFilter ? "pr-10" : "pr-12")
                  ),
              "disabled:cursor-not-allowed disabled:opacity-50",
            )}
            placeholder={
              embedded
                ? props.placeholder
                : label
                  ? " "
                  : props.placeholder
            }
            onFocus={(e) => {
              if (!embedded) setIsFocused(true);
              onFocus?.(e);
            }}
            onBlur={(e) => {
              if (!embedded) setIsFocused(false);
              onBlur?.(e);
            }}
            onChange={(e) => {
              setHasValue(Boolean(e.target.value));
              onChange?.(e);
            }}
            onAnimationStart={(e) => {
              // El navegador autocompleta sin disparar onChange; usamos la
              // animación CSS de :-webkit-autofill para flotar el label.
              if (e.animationName === "onAutoFillStart") {
                setHasValue(true);
              } else if (e.animationName === "onAutoFillCancel") {
                setHasValue(Boolean(value ?? e.currentTarget.value));
              }
              onAnimationStart?.(e);
            }}
            {...props}
          />

          {label && !embedded && (
            <label
              htmlFor={inputId}
              className={cn(
                "pointer-events-none absolute z-20 select-none font-medium transition-all duration-200",
                leftIcon ? (isXxs ? "left-7" : isXs ? "left-8" : isSm || isFilter ? "left-9" : "left-10") : (isXxs ? "left-2" : isXs ? "left-2.5" : isSm || isFilter ? "left-3" : "left-4"),
                isFloating
                  ? cn(
                      isXxs
                        ? "top-0 -translate-y-1/2 rounded-2xl bg-white/90 px-1 py-0.5 text-[8px] leading-none dark:bg-zinc-950/90"
                        : isXs
                          ? "top-0 -translate-y-1/2 rounded-2xl bg-white/90 px-1.5 py-0.5 text-[9px] leading-none dark:bg-zinc-950/90"
                          : isSm || isFilter
                            ? "top-0 -translate-y-1/2 rounded-2xl bg-white/90 px-1.5 py-0.5 text-[10px] leading-none dark:bg-zinc-950/90"
                            : "top-0 -translate-y-1/2 rounded-2xl bg-white/90 px-2.5 py-1 text-xs leading-none dark:bg-zinc-950/90",
                      colors.label
                    )
                  : cn(
                      "top-1/2 -translate-y-1/2 cursor-text",
                      isXxs ? "text-[9px]" : isXs ? "text-[10px]" : isSm || isFilter ? "text-xs" : "text-sm",
                      "text-muted-foreground"
                    )
              )}
            >
              {label}
            </label>
          )}

          {rightIcon && (
            <div
              className={cn(
                "absolute inset-y-0 flex items-center text-muted-foreground",
                isXxs ? "right-2" : isXs ? "right-2.5" : isSm || isFilter ? "right-3" : "right-3.5"
              )}
            >
              {rightIcon}
            </div>
          )}

          {hasTopEnd && !embedded && (
            <div
              className={cn(
                "absolute z-30",
                isXxs ? "right-1 top-1" : isXs ? "right-1.5 top-1.5" : isSm || isFilter ? "right-1.5 top-1.5" : "right-2.5 top-2.5"
              )}
            >
              {topEnd}
            </div>
          )}
        </div>

        {(error ?? helperText) && (
          <p
            className={cn(
              "mt-1.5 px-1 text-xs",
              error ? "text-destructive" : "text-muted-foreground"
            )}
          >
            {error ?? helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = "Input";

export { Input };
